import { React, useState, useEffect, useMemo } from "react";

import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";

import { Search } from "@mui/icons-material";

import { useGridApiRef } from "@mui/x-data-grid";

import api from "../../services/api";
import ButtonGoBack from "../../components/Buttons/outlinedGoback";
import TableGridData from "../../components/TableGridData";
import ModalDialogs from "../../components/ModalDialogs";
import { FieldInputMask, FieldInputText } from "../../components/Fields";
import { FormControl, FormControlLabel } from "@mui/material";
import { getItemDecrypt } from "../../utils/cryptoData";

const setColor = (value) => {
  const colorMap = {
    Homologação: "#063F5E",
    Ativo: "#3AB238",
    Inativo: "#595959",
  };
  return colorMap[value] || "#595959";
};

const setBgColor = (value) => {
  const colorMap = {
    Homologação: "#B2D0E0",
    Ativo: "#C8ECC7",
    Inativo: "#B2B2B2",
  };
  return colorMap[value] || "none";
};

const columns = [
  {
    field: "id",
    headerName: "ID",
    width: 0,
    sortable: false,
    hide: true,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      return (
        <Typography
          sx={{
            color: setColor(params.row.status),
            backgroundColor: setBgColor(params.row.status),
            borderRadius: "30px",
            width: "136px",
            textAlign: "center",
            padding: "5px",
          }}
        >
          {params.row.status}
        </Typography>
      );
    },
  },
  {
    field: "nome",
    headerName: "Nome",
    flex: 1,
    minWidth: 180,
    sortable: true,
  },
  {
    field: "perfil",
    headerName: "Perfil",
    minWidth: 170,
    flex: 1,
    sortable: false,
  },
];

const styles = {
  title: {
    fontWeight: "600",
    fontSize: "26px",
    lineHeight: "35px",
    color: "#063F5E",
    fontFamily: "Nunito, sans-serif ",
  },
  subtitle: {
    color: "#7D7D7D",
    fontWeight: "600",
    fontSize: "24px",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "24px",
    padding: "1rem",
    boxShadow: "0px 0px 5px 4px rgba(217,217,217,0.3)",
  },
  homolog: {
    color: "#063F5E",
  },
  ativo: {
    color: "#3AB238",
  },
  inativo: {
    color: "#595959",
  },
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function ListagemUsuarios() {
  const [rows, setRows] = useState([]);
  const apiRef = useGridApiRef(null);

  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const [tab, setTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [openDetails, setOpenDetails] = useState(false);
  const [openHomolog, setOpenHomolog] = useState(false);
  const [openInactive, setOpenInactive] = useState(false);
  const [openSavePermission, setOpenSavePermission] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vinculoSelecionado, setVinculoSelecionado] = useState(0);
  const [administrador, setAdministrador] = useState(false);
  const [openSucess, setOpenSucess] = useState(false);

  useEffect(() => {
    getDados();
  }, []);

  async function getDados() {
    try {
      setLoading(true);
      const { data } = await api.post("/vinculosPorCNES/", {
        id_vinculo: parseInt(getItemDecrypt("vinculo_ativo").id),
      });
      if (columns[columns.length - 1].field !== "acoes") {
        columns.push({
          field: "acoes",
          headerName: "Ações",
          width: 140,
          headerClassName: "color-blue",
          description: "This column has a value getter and is not sortable.",
          sortable: false,
          renderCell: (params) => {
            return detalhesButton(params.row);
          },
        });
      }
      setRows(data);
      setLoading(false);
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  const handleConfirmaHomologacao = async () => {
    if (isSend) return;
    setIsSend(true);
    try {
      await api.put("/statusVinculo/", {
        data: {
          id_vinculo: vinculoSelecionado?.id,
          status: 1,
          id_user_authorizer: parseInt(getItemDecrypt("usuario").id),
          st_administrador: administrador,
        },
      });
      getDados();
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    } finally {
      setIsSend(false);
      setOpenSucess(true);
      handleCloseDetails();
    }
  };

  const handleConfirmarInativar = async () => {
    if (isSend) return;
    setIsSend(true);
    try {
      await api.put("/statusVinculo/", {
        data: {
          id_vinculo: vinculoSelecionado?.id,
          status: 2,
          st_administrador: false,
          id_user_authorizer: parseInt(getItemDecrypt("usuario").id),
        },
      });
      getDados();
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    } finally {
      setIsSend(false);
      setOpenSucess(true);
      handleCloseDetails();
    }
  };

  const handleCloseDetails = () => {
    if (
      openDetails &&
      vinculoSelecionado.administrador !== administrador &&
      !openHomolog &&
      !openInactive &&
      !openSavePermission
    ) {
      setOpenSavePermission(true);
    } else {
      setOpenSavePermission(false);
      setOpenDetails(false);
      setOpenHomolog(false);
      setOpenInactive(false);
    }
  };

  const handleOpenHomolog = () => {
    setOpenHomolog(true);
  };

  const handleOpenDetails = (item) => {
    setIsLoadingUser(true);

    handleVinculoSelecionado(item);
  };

  const handleVinculoSelecionado = async (item) => {
    const { data } = await api.post(`/vinculo/${item.id}`);
    setVinculoSelecionado({ ...data, status: item.status });
    setAdministrador(data?.administrador);
    setOpenDetails(true);
    setIsLoadingUser(false);
  };

  const handleOpenInactive = () => {
    setOpenInactive(true);
  };

  function updateSearchValue(newValue) {
    apiRef.current.setQuickFilterValues(
      newValue.split(" ").filter((word) => word !== "")
    );
  }
  function handleSearchValueChange(event) {
    const newValue = event.target.value;
    setSearchValue(newValue);
    updateSearchValue(newValue);
  }

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  const detalhesButton = (item) => {
    return (
      <Button
        onClick={() => {
          handleOpenDetails(item);
        }}
        variant="contained"
        sx={{
          textTransform: "none",
          backgroundColor: "#1E729F",
          "&:hover": { backgroundColor: "#063F5E" },
        }}
      >
        Ver detalhes
      </Button>
    );
  };

  const toalAll = useMemo(() => {
    return rows.length;
  }, [rows]);
  const totalActive = useMemo(() => {
    return rows.filter((vinculo) => vinculo.status === "Ativo").length;
  }, [rows]);
  const totalDisabled = useMemo(() => {
    return rows.filter((vinculo) => vinculo.status === "Inativo").length;
  }, [rows]);
  const totalHomolog = useMemo(() => {
    return rows.filter((vinculo) => vinculo.status === "Homologação").length;
  }, [rows]);

  const data = useMemo(() => {
    return rows.filter((vinculo) => {
      if (tab === 0) {
        return vinculo;
      } else if (tab === 1) {
        return vinculo.status === "Homologação";
      } else if (tab === 2) {
        return vinculo.status === "Ativo";
      } else if (tab === 3) {
        return vinculo.status === "Inativo";
      }
    });
  }, [rows, tab]);

  return (
    <>
      {!loading && (
        <Box paddingBottom={"50px"}>
          <Stack direction={"column"} justifyContent="center">
            <Box>
              <ButtonGoBack />
              <Box mb={4} style={styles.title}>
                Listagem de usuários
              </Box>
            </Box>
            <Paper style={styles.paper}>
              <Stack gap={3}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    TabIndicatorProps={{
                      style: {
                        background: "#1E729F",
                      },
                    }}
                    value={tab}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      justifyContent: "space-around",
                      "& .Mui-selected": { color: "#1E729F" },
                    }}
                  >
                    <Tab
                      className="tabTitle"
                      label={`Todos (${toalAll})`}
                      {...a11yProps(0)}
                      TabIndicatorProps={{
                        style: {
                          background: "#1E729F",
                        },
                      }}
                    />
                    <Tab
                      className="tabTitle"
                      label={`Aguardando homologação (${totalHomolog})`}
                      {...a11yProps(1)}
                    />
                    <Tab
                      className="tabTitle"
                      label={`Ativo (${totalActive})`}
                      {...a11yProps(2)}
                    />
                    <Tab
                      className="tabTitle"
                      label={`Inativo (${totalDisabled})`}
                      {...a11yProps(2)}
                    />
                  </Tabs>
                </Box>
                <Box
                  component="form"
                  sx={{
                    p: "2px 4px",
                    display: "flex",
                    alignItems: "center",
                    height: "40px",
                    border: "1px solid #3E94C2",
                    boxShadow: "none",
                    borderRadius: "5px",
                  }}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder="Procurar por nome"
                    inputProps={{ "aria-label": "procurar por nome" }}
                    value={searchValue}
                    onChange={handleSearchValueChange}
                  />
                  <IconButton
                    type="button"
                    sx={{ p: "10px" }}
                    aria-label="search"
                  >
                    <Search sx={{ color: "#3E94C2" }} />
                  </IconButton>
                </Box>
                <Box position={"relative"}>
                  {isLoadingUser && (
                    <LinearProgress
                      sx={{
                        color: "#1E729F",
                        borderRadius: "5px",
                        position: "absolute",
                        zIndex: 10,
                        width: "100%",
                      }}
                    />
                  )}

                  <TableGridData
                    rows={data}
                    columns={columns}
                    apiRef={apiRef}
                    getRowClassName={(params) =>
                      params.indexRelativeToCurrentPage % 2 === 0
                        ? "Mui-odd"
                        : "Mui-odd"
                    }
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      )}
      <ModalDialogs
        open={openDetails}
        onClose={handleCloseDetails}
        title="Detalhes"
        maxWidth="900px"
        {...(["Homologação", "Inativo"].includes(vinculoSelecionado.status) && {
          primaryButtonText: "Homologar",
        })}
        {...(["Homologação", "Ativo"].includes(vinculoSelecionado.status) && {
          secondaryButtonText: "Inativar",
        })}
        onActionPrimary={handleOpenHomolog}
        onActionSecondary={handleOpenInactive}
        body={
          <Stack
            textAlign="start"
            gap={2}
            width={{ xs: "100%", sm: "500px", md: "800px" }}
          >
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems={"center"}
              gap={2}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "20px",
                  color: "#063F5E",
                }}
              >
                Informações pessoais
              </Typography>
              <Typography
                sx={{
                  color: setColor(vinculoSelecionado.status),
                  backgroundColor: setBgColor(vinculoSelecionado.status),
                  borderRadius: "30px",
                  width: "136px",
                  textAlign: "center",
                  padding: "3px",
                }}
              >
                {vinculoSelecionado.status}
              </Typography>
            </Stack>

            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems={"center"}
              gap={2}
            >
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "20px",
                    color: "#063F5E",
                  }}
                >
                  Estabelecimento
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "22px",
                    color: "#737373",
                    textTransform: "capitalize",
                  }}
                >
                  {vinculoSelecionado?.estabelecimento?.nome}
                </Typography>
              </Stack>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "20px",
                    color: "#063F5E",
                  }}
                >
                  Local
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "22px",
                    color: "#737373",
                    textTransform: "capitalize",
                  }}
                >
                  {vinculoSelecionado?.uf?.nome} /{" "}
                  {vinculoSelecionado?.uf?.sigla} <br />
                  {vinculoSelecionado?.municipio?.nome}
                </Typography>
              </Stack>
            </Stack>
            <Grid
              container
              gap={"24px 16px"}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                boxSizing: "border-box !important",
                textAlign: "left",
              }}
              width="100%"
            >
              <Grid item>
                <FieldInputText
                  label="Nome"
                  value={vinculoSelecionado?.usuario?.nome}
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="Perfil"
                  value={vinculoSelecionado?.perfil?.nome}
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="CBO"
                  value={vinculoSelecionado?.usuario?.cbo}
                  placeholder="0000000"
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="Conselho"
                  value={vinculoSelecionado?.usuario?.conselho}
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="Email"
                  value={vinculoSelecionado?.usuario?.email}
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputMask
                  label="Telefone"
                  mask="(99) 99999-9999"
                  placeholder="(99) 99999-9999"
                  value={vinculoSelecionado?.usuario?.telefone}
                  setValue={(value) => {}}
                  type={"tel"}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="Cidade"
                  value={vinculoSelecionado?.municipio?.nome}
                  setValue={(value) => {}}
                />
              </Grid>
              <Grid item>
                <FieldInputText
                  label="Estado"
                  value={vinculoSelecionado?.uf?.nome}
                  setValue={(value) => {}}
                />
              </Grid>
            </Grid>
            <FormControl
              sx={{
                width: "fit-content",
              }}
            >
              <FormControlLabel
                label="Administrador"
                control={
                  <Checkbox
                    checked={administrador}
                    onChange={(event) => {
                      setAdministrador(event.target.checked);
                    }}
                    sx={{
                      color: "#3E94C2",
                    }}
                  />
                }
              />
            </FormControl>
          </Stack>
        }
      />

      <ModalDialogs
        open={openInactive}
        onClose={() => setOpenInactive(false)}
        title="Inativar usuário"
        secondaryButtonText="Cancelar"
        primaryButtonText="Confirmar"
        onActionPrimary={handleConfirmarInativar}
        onActionSecondary={handleCloseDetails}
        body={
          <Box width={{ xs: "10%%", sm: "500px" }}>
            {isSend && <CircularProgress />}
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "22px",
                color: "#063F5E",
                lineHeight: "30px",
                p: 2,
              }}
            >
              Você realmente deseja inativar <br /> esse usuário?
            </Typography>
          </Box>
        }
      ></ModalDialogs>

      <ModalDialogs
        open={openHomolog}
        onClose={() => setOpenHomolog(false)}
        title="Homologar usuário"
        secondaryButtonText="Cancelar"
        primaryButtonText="Confirmar"
        onActionPrimary={handleConfirmaHomologacao}
        onActionSecondary={handleCloseDetails}
        body={
          <Box width={{ xs: "10%%", sm: "500px" }}>
            {isSend && <CircularProgress />}
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "22px",
                color: "#063F5E",
                lineHeight: "30px",
                p: 2,
              }}
            >
              Você realmente deseja <br /> homologar esse usuário?
            </Typography>
          </Box>
        }
      ></ModalDialogs>

      <ModalDialogs
        open={openSucess}
        onClose={() => setOpenSucess(false)}
        title="Sucesso"
        modalType={"success"}
        primaryButtonText="Fechar"
        onActionPrimary={() => setOpenSucess(false)}
        body={
          <Box width={{ xs: "10%%", sm: "500px" }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "22px",
                lineHeight: "30px",
              }}
            >
              Usuário alterado com sucesso.
            </Typography>
          </Box>
        }
      ></ModalDialogs>

      <ModalDialogs
        open={openSavePermission}
        onClose={() => setOpenSavePermission(false)}
        title="Permissão de Administrado"
        secondaryButtonText="Cancelar"
        primaryButtonText="Confirmar"
        onActionPrimary={handleConfirmaHomologacao}
        onActionSecondary={handleCloseDetails}
        body={
          <Box width={{ xs: "10%%", sm: "500px" }}>
            {isSend && <CircularProgress />}
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "22px",
                color: "#063F5E",
                lineHeight: "30px",
                p: 2,
              }}
            >
              {administrador ? (
                <>
                  Você realmente deseja conceder <br />a permissão de
                  administrador a este usuário?
                </>
              ) : (
                <>
                  Você realmente deseja revogar <br />a permissão de
                  administrador deste usuário?
                </>
              )}
            </Typography>
          </Box>
        }
      ></ModalDialogs>
    </>
  );
}
